import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import type { RootState } from '@/app/store/store';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles?: string[];
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication
 * Redirects to /login if user is not authenticated
 * Optionally checks for specific user roles
 *
 * @param children - The component to render if authenticated
 * @param requiredRoles - Optional array of roles required to access the route
 *
 * @example
 * <ProtectedRoute>
 *     <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * <ProtectedRoute requiredRoles={['admin', 'manager']}>
 *     <AdminPanel />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
    const isAuthenticated = useSelector((state: RootState) => state.authStore.isAuthenticated);

    const userRoles = useSelector((state: RootState) => state.authStore.user?.roles);

    // CHECK IF USER IS AUTHENTICATED
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // CHECK IF USER HAS REQUIRED ROLES (IF SPECIFIED)
    if (requiredRoles && requiredRoles.length > 0) {
        const userRoleArray = userRoles ? userRoles.split(',').map((r: string) => r.trim()) : [];
        const hasRequiredRole = requiredRoles.some((role) => userRoleArray.includes(role));

        if (!hasRequiredRole) {
            return <Navigate to="/login" replace />;
        }
    }
    return <>{children}</>;
};

export default ProtectedRoute;
